import { CareerPath, CareerPathResponse } from './careerPath';
import { College } from './college';
import { ExternalProfile } from './externalProfile';
import { Internship } from './internship';
import { BackrsPost } from './post';
import { Scholarship } from './scholarship';
import { BackrsUser } from './user';

type ProficiencyLevel = 0 | 1 | 2;

export type ContentCompetencyProficiency = Readonly<{
  content_competency_id: number;
  proficiency: ProficiencyLevel;
}>;

export type ContentCategory = Readonly<{
  id: number;
  title: string;
  sequence: number;
}>;

export type ContentCompetency = Readonly<{
  id: number;
  content_category_id: number;
  title: string;
  sequence: number;
  description: string;
  slug: string;
}>;

/**
 * NUGGET TYPES
 */

export const NUGGET_THEMES = {
  REAL_PEOPLE_REAL_STORIES: 'Real People, Real Stories',
  LEARN_AND_EXPLORE: 'Learn & Explore',
  GROW_BY_DOING: 'Grow by Doing',
} as const;

export type NuggetTheme = (typeof NUGGET_THEMES)[keyof typeof NUGGET_THEMES];

/* The string should match the variants in the database  */
export const NUGGET_VARIANTS = {
  ARTICLE: 'article',
  VIDEO_EXTERNAL: 'video-external',
  VIDEO_INTERNAL: 'video-internal',
  TEXT_RESPONSE: 'text-response',
  POST: 'post',
  AUDIO: 'audio',
  DOCUMENT: 'document',
  PROFILE_EXTERNAL: 'profile-external',
  PROFILE_INTERNAL: 'profile-internal',
  INSTITUTION: 'institution',
  OPPORTUNITY: 'opportunity',
  EVENT: 'event',
  BOOK: 'book',
  COMPETENCY: 'competency',
  SCHOLARSHIPS: 'scholarships',
  CAREER_PATH: 'career-path',
  INTERNSHIP: 'internship',
  RESOURCE: 'resource',
} as const;

export type NuggetVariant = (typeof NUGGET_VARIANTS)[keyof typeof NUGGET_VARIANTS];

export const NUGGET_TYPES = {
  BACKRS_PROFILE_PROFESSIONAL: 'backrs-profile-professional',
  BACKRS_PROFILE_STUDENT: 'backrs-profile-student',
  BACKRS_PROFILE_SERVICE_PROVIDER: 'backrs-profile-service-provider',
  EXTERNAL_PROFILE_PROFESSIONAL: 'external-profile-professional',
  EXTERNAL_PROFILE_STUDENT: 'external-profile-student',
  EXTERNAL_PROFILE_SERVICE_PROVIDER: 'external-profile-service-provider',
  INSTITUTION_HIGH_SCHOOL: 'institution-high-school',
  INSTITUTION_UNIVERSITY: 'institution-university',
  INSTITUTION_EMPLOYER: 'institution-employer',
  INSTITUTION_NONPROFIT: 'institution-nonprofit',
  INSTITUTION_OTHER: 'institution-other',
  NETWORK: 'network',
  CONTENT: 'content',
  ACTIVITY: 'activity',
  OPPORTUNITY: 'opportunity',
} as const;

export type NuggetType = (typeof NUGGET_TYPES)[keyof typeof NUGGET_TYPES];

export const NUGGET_FORMATS = {
  VIDEO: 'video',
  EVENT: 'event',
  AUDIO_FILE: 'audio-file',
  RESUMES_CV: 'resumes-cv',
  TRANSCRIPT: 'transcript',
  DOCUMENT: 'document',
  BOOK: 'book',
  PRODUCT: 'product',
  SERVICE: 'service',
  APP: 'app',
  COURSE: 'course',
  PROFILE: 'profile',
  POST: 'post',
  ARTICLE: 'article',
  PODCAST: 'podcast',
  URL_DESCRIPTION: 'url-description',
  SHADOWING: 'shadowing',
  INTERNSHIP: 'internship',
  FULL_TIME_POSITION: 'full-time-position',
  RESEARCH: 'research',
  VOLUNTEERING: 'volunteering',
  ENRICHMENT: 'enrichment',
  SCHOLARSHIPS: 'scholarships',
} as const;

export type NuggetFormat = (typeof NUGGET_FORMATS)[keyof typeof NUGGET_FORMATS];

export type ContentNugget = Readonly<{
  id: number;
  slug: string;
  title: string;
  description: string;
  external_url?: string;
  star_rating: number;
  value_rating: number;
  media_id?: number;
  media?: {
    id: number;
    media_url: string;
  };
  created_by_user_id?: number;
  sponsor_id?: number;
  start_date?: string;
  end_date?: string;
  variant: NuggetVariant;
  content_nugget_theme?: NuggetTheme | string;
  format?: NuggetFormat;
  type?: NuggetType;
  tags?: string;
  preview_media_id?: number;
  preview_media?: {
    id: number;
    media_url: string;
  };
  is_browsable: boolean;

  internship_info?: Internship;
  scholarship_info?: Scholarship;
  profile_info?: BackrsUser;
  career_path_info?: CareerPath;
  post_info?: BackrsPost;
  external_profile_info?: ExternalProfile;
  college_info?: College;
}>;

export const NUGGET_VALIDATION_TYPES = {
  BACKRS: 'Backrs',
  USER: 'User',
  EXPERT: 'Expert',
  SPONSOR: 'Sponsor',
};

export type NuggetValidationType =
  (typeof NUGGET_VALIDATION_TYPES)[keyof typeof NUGGET_VALIDATION_TYPES];

export const ASK_LUBAV_BUTTON_ENDPOINTS = {
  CAREER_TRACK_THREE_ROLES_FOR_ME: 'career-track/three-roles-for-me',
  CAREER_TRACK_ROLES_BY_TRACK: 'career-track/roles-by-track',
  CAREER_TRACK_COMPENSATION: 'career-track/compensation',
  CAREER_TRACK_REQUIREMENTS_TO_SUCCEED: 'career-track/requirements-to-succeed',
  CAREER_TRACK_AM_I_ON_TRACK: 'career-track/am-i-on-track',
  CAREER_TRACK_AI_DISRUPTION: 'career-track/ai-disruption',
  INTERNSHIP_AM_I_A_GOOD_FIT: 'internship/am-i-a-good-fit',
  INTERNSHIP_SIMILAR_ROLES: 'internship/similar-roles',
  INTERNSHIP_INCREASE_MY_ODDS: 'internship/increase-my-odds',
  CAREER_PATH_FIT_EXPLAINER: 'career-track/explain-career-path-fit',

  COLLEGE_AM_I_A_GOOD_FIT: 'college/am-i-a-good-fit',
  COLLEGE_EXAMPLE_PROFILES: 'college/example-extracurricular-profiles',
  COLLEGE_WHAT_DO_STUDENTS_SAY: 'college/what-do-students-say',
  COLLEGE_CAMPUS_LIFE: 'college/campus-life',
  COLLEGE_HOW_CAN_I_LEARN_MORE: 'college/how-can-i-learn-more',
  COLLEGE_IMPROVE_MY_ODDS: 'college/improve-my-odds',
} as const;

export type AskLubavButtonEndpoint =
  (typeof ASK_LUBAV_BUTTON_ENDPOINTS)[keyof typeof ASK_LUBAV_BUTTON_ENDPOINTS];

export type ContentNuggetBookmarkTypes =
  | 'nugget'
  | 'scholarship'
  | 'career-path'
  | 'profile'
  | 'external-profile'
  | 'internship'
  | 'post'
  | 'college';

export type ContentNuggetBookmark = Readonly<{
  id: number;
  user_id: number;
  content_nugget?: ContentNugget;
  content_scholarship?: Scholarship;
  content_profile?: BackrsUser;
  content_internship?: Internship;
  content_career_path: CareerPathResponse;
  content_external_profile?: ExternalProfile;
  content_college?: College;
  content_post?: BackrsPost;
  created_at: string;
  updated_at: string;
}>;
